// صفحات عمومی DOC (بدون ورود): SCR-PUBLIC-010 تکمیل فرم پیش از مراجعه (/f/:token)، SCR-PUBLIC-011 امضای از راه دور (/s/:token) و
// SCR-PUBLIC-012 مشاهده سند اشتراکی (/d/:token). هویت با OTP موبایل اصلی (یا ولی قانونی) تأیید می‌شود (AC-DOC-032.2، 042.1، 060.1)؛ توکن نشست
// کوتاه‌عمر فقط در حافظه صفحه نگه داشته می‌شود (نه localStorage). لینک نامعتبر یا منقضی ← پیام عمومی بدون جزئیات.
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { isApiError, unwrap, type DocPublicLinkInfo, type DocPublicOtpChallenge } from '@phoenix/api-client';
import { usePublicApi } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { type Answers, asDefinition, cleanAnswers, ErrorState, faDigits, FormRenderer, JalaliDate, latinDigits, LoadingState, missingRequired, SampleBadge, SignaturePad, type SignatureValue } from '@phoenix/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useParams } from 'react-router';
import { SCR_PUBLIC_010, SCR_PUBLIC_011, SCR_PUBLIC_012 } from '../common';
import { ConsentReader, type ConsentReadState } from '../ConsentSigning';
import { FileView } from '../DocumentViewer';

type Segment = 'forms' | 'signatures' | 'shares';
const SESSION_HEADER = 'X-Document-Session';

function Frame({ screenId, title, tenantName, children }: { screenId: string; title: string; tenantName?: string; children: ReactNode }) {
  const { t } = useTranslation('doc');
  useEffect(() => {
    document.title = `${title} · ${t('shell:app.staff_name')}`;
  }, [t, title]);
  return (
    <Box data-screen-id={screenId} sx={{ minHeight: '100vh', bgcolor: 'background.default', p: { xs: 1.5, sm: 3 } }}>
      <Paper variant="outlined" component="main" sx={{ p: { xs: 2, sm: 4 }, maxWidth: 860, mx: 'auto' }}>
        <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: 'center' }}>
          <LocalHospitalOutlinedIcon color="primary" />
          <Typography sx={{ fontWeight: 600 }} data-testid="public-tenant-name">
            {tenantName ?? t('shell:app.staff_name')}
          </Typography>
        </Stack>
        <Typography variant="h1" sx={{ mb: 2 }}>
          {title}
        </Typography>
        {children}
      </Paper>
    </Box>
  );
}

/** اطلاعات لینک و تأیید OTP؛ پس از تأیید، فرزند با توکن نشست رندر می‌شود. */
function OtpGate({ segment, token, info, children }: { segment: Segment; token: string; info: DocPublicLinkInfo; children: (session: string) => ReactNode }) {
  const { t } = useTranslation('doc');
  const api = usePublicApi();
  const [challenge, setChallenge] = useState<DocPublicOtpChallenge | null>(null);
  const [code, setCode] = useState('');
  const [session, setSession] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: () => unwrap(api.POST(`/api/v1/documents/public/${segment}/{token}:send-otp` as '/api/v1/documents/public/forms/{token}:send-otp', { params: { path: { token } } })),
    onSuccess: setChallenge,
  });
  const verify = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST(`/api/v1/documents/public/${segment}/{token}:verify` as '/api/v1/documents/public/forms/{token}:verify', {
          params: { path: { token } },
          body: { otpId: challenge?.otpId ?? null, code: latinDigits(code) },
        }),
      ),
    onSuccess: (result) => setSession(result.sessionToken),
  });

  if (session) {
    return <>{children(session)}</>;
  }
  return (
    <Stack spacing={2} data-testid="public-otp">
      <Typography>{t('doc:public.otp_intro', { mobile: faDigits(info.maskedMobile ?? '') })}</Typography>
      {!challenge ? (
        <Button variant="contained" onClick={() => send.mutate()} disabled={send.isPending} data-testid="public-send-otp" sx={{ alignSelf: 'flex-start' }}>
          {t('doc:public.send_otp')}
        </Button>
      ) : (
        <Stack spacing={1.5}>
          <TextField
            label={t('doc:public.otp_code')}
            value={code}
            onChange={(event) => setCode(event.target.value)}
            slotProps={{ htmlInput: { 'data-testid': 'public-otp-code', inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: 6, dir: 'ltr' } }}
          />
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={() => verify.mutate()} disabled={code.length < 4 || verify.isPending} data-testid="public-verify-otp">
              {t('doc:public.verify')}
            </Button>
            <Button onClick={() => send.mutate()} disabled={send.isPending}>
              {t('doc:public.resend')}
            </Button>
          </Stack>
        </Stack>
      )}
      {send.error ? <ErrorState error={send.error} compact /> : null}
      {verify.error ? <ErrorState error={verify.error} compact /> : null}
    </Stack>
  );
}

function useLinkInfo(segment: Segment, token: string) {
  const api = usePublicApi();
  return useQuery({
    queryKey: ['documents-public', segment, token],
    queryFn: () => unwrap(api.GET(`/api/v1/documents/public/${segment}/{token}` as '/api/v1/documents/public/forms/{token}', { params: { path: { token } } })),
    retry: false,
  });
}

function LinkState({ info, error, children, doneKey }: { info: DocPublicLinkInfo | undefined; error: unknown; children: ReactNode; doneKey: string }) {
  const { t } = useTranslation('doc');
  if (error) {
    return (
      <Alert severity="warning" data-testid="public-invalid">
        {isApiError(error) && error.status === 404 ? t('doc:public.invalid') : <ErrorState error={error} compact />}
      </Alert>
    );
  }
  if (!info) {
    return <LoadingState rows={3} />;
  }
  if (info.status !== 'Open') {
    return (
      <Alert severity="info" data-testid="public-done">
        {t(doneKey)}
      </Alert>
    );
  }
  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {t('doc:public.valid_until')} <JalaliDate value={info.expiresAt} withTime />
      </Typography>
      {children}
    </Stack>
  );
}

function PublicFormBody({ token, session }: { token: string; session: string }) {
  const { t } = useTranslation('doc');
  const api = usePublicApi();
  const headers = { [SESSION_HEADER]: session };
  const form = useQuery({
    queryKey: ['documents-public', 'form', token],
    queryFn: () => unwrap(api.GET('/api/v1/documents/public/forms/{token}/form', { params: { path: { token } }, headers })),
  });
  const [answers, setAnswers] = useState<Answers>({});
  const [missing, setMissing] = useState<string[]>([]);
  const [saved, setSaved] = useState(false);
  const dirty = useRef(false);
  useEffect(() => {
    if (form.data && !dirty.current) {
      setAnswers((form.data.answers as Answers | null) ?? {});
    }
  }, [form.data]);
  const definition = asDefinition(form.data?.template.definition);
  const patient = { gender: form.data?.patient.gender, age: form.data?.patient.age };
  const save = useMutation({
    mutationFn: (current: Answers) => unwrap(api.PUT('/api/v1/documents/public/forms/{token}/form', { params: { path: { token } }, headers, body: { answers: current as never } })),
    onSuccess: () => setSaved(true),
  });
  const { mutate: autosave } = save;
  useEffect(() => {
    if (!dirty.current) {
      return undefined;
    }
    const handle = setTimeout(() => autosave(cleanAnswers(definition, answers, patient)), 1500);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, autosave]);
  const submit = useMutation({
    mutationFn: () => unwrap(api.POST('/api/v1/documents/public/forms/{token}/form:submit', { params: { path: { token } }, headers, body: { answers: cleanAnswers(definition, answers, patient) as never } })),
  });

  if (form.error) {
    return <ErrorState error={form.error} />;
  }
  if (!form.data) {
    return <LoadingState rows={4} />;
  }
  if (submit.isSuccess) {
    return (
      <Alert severity="success" data-testid="public-form-submitted">
        {t('doc:public.form_submitted')}
      </Alert>
    );
  }
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography variant="h6" component="h2">
          {form.data.template.name}
        </Typography>
        {form.data.template.isSample ? <SampleBadge /> : null}
      </Stack>
      <FormRenderer
        definition={definition}
        answers={answers}
        onChange={(next) => {
          dirty.current = true;
          setSaved(false);
          setAnswers(next);
        }}
        patient={patient}
        errors={missing}
        testPrefix="public-field"
      />
      {missing.length > 0 ? <Alert severity="error">{t('doc:forms.missing', { count: missing.length })}</Alert> : null}
      {submit.error ? <ErrorState error={submit.error} compact /> : null}
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <Button
          variant="contained"
          size="large"
          data-testid="public-form-submit"
          disabled={submit.isPending}
          onClick={() => {
            const required = missingRequired(definition, answers, patient);
            setMissing(required);
            if (required.length === 0) {
              submit.mutate();
            }
          }}
        >
          {t('doc:forms.submit')}
        </Button>
        {saved ? (
          <Typography variant="caption" color="text.secondary">
            {t('doc:forms.autosaved')}
          </Typography>
        ) : null}
      </Stack>
    </Stack>
  );
}

export function PublicFormPage() {
  const { t } = useTranslation('doc');
  const { token = '' } = useParams();
  const info = useLinkInfo('forms', token);
  return (
    <Frame screenId={SCR_PUBLIC_010} title={t('doc:public.form_title')} tenantName={info.data?.tenantName}>
      <LinkState info={info.data} error={info.error} doneKey="doc:public.form_done">
        {info.data ? <OtpGate segment="forms" token={token} info={info.data}>{(session) => <PublicFormBody token={token} session={session} />}</OtpGate> : null}
      </LinkState>
    </Frame>
  );
}

function PublicSignBody({ token, session }: { token: string; session: string }) {
  const { t } = useTranslation('doc');
  const api = usePublicApi();
  const headers = { [SESSION_HEADER]: session };
  const subject = useQuery({
    queryKey: ['documents-public', 'subject', token],
    queryFn: () => unwrap(api.GET('/api/v1/documents/public/signatures/{token}/subject', { params: { path: { token } }, headers })),
  });
  const document = useQuery({
    queryKey: ['documents-public', 'subject-document', token],
    queryFn: async () => {
      const result = await api.GET('/api/v1/documents/public/signatures/{token}/document', { params: { path: { token } }, headers, parseAs: 'blob' });
      if (!result.response.ok) {
        throw (result.error as unknown) ?? new Error('document');
      }
      return result.data as unknown as Blob;
    },
    enabled: !!subject.data?.documentUrl,
  });
  const [read, setRead] = useState<ConsentReadState>({ scrolledToEnd: false, acceptedClauses: [] });
  const [signature, setSignature] = useState<SignatureValue | null>(null);
  const [mode, setMode] = useState<'draw' | 'otp'>('draw');
  const [otp, setOtp] = useState<DocPublicOtpChallenge | null>(null);
  const [code, setCode] = useState('');
  const sendSigning = useMutation({
    mutationFn: () => unwrap(api.POST('/api/v1/documents/public/signatures/{token}:send-signing-otp', { params: { path: { token } }, headers })),
    onSuccess: setOtp,
  });
  const sign = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/api/v1/documents/public/signatures/{token}:sign', {
          params: { path: { token } },
          headers,
          body: {
            scrolledToEnd: subject.data?.text ? read.scrolledToEnd : true,
            acceptedClauses: read.acceptedClauses,
            image: mode === 'draw' ? (signature?.image ?? null) : null,
            strokes: mode === 'draw' ? ((signature?.strokes ?? null) as never) : null,
            otpId: mode === 'otp' ? (otp?.otpId ?? null) : null,
            code: mode === 'otp' ? latinDigits(code) : null,
          },
        }),
      ),
  });
  if (subject.error) {
    return <ErrorState error={subject.error} />;
  }
  if (!subject.data) {
    return <LoadingState rows={4} />;
  }
  if (sign.isSuccess) {
    return (
      <Alert severity="success" data-testid="public-signed">
        {t('doc:public.signed')}
      </Alert>
    );
  }
  const text = subject.data.text;
  const requiredClauses = text?.clauses.filter((clause) => clause.required).map((clause) => clause.key) ?? [];
  const readOk = !text || (read.scrolledToEnd && requiredClauses.every((key) => read.acceptedClauses.includes(key)));
  const signOk = mode === 'draw' ? signature !== null : otp !== null && code.length >= 4;
  return (
    <Stack spacing={2} data-testid="public-sign">
      {text ? <ConsentReader text={text} isSample={subject.data.isSample} onChange={setRead} /> : null}
      {!text && subject.data.isSample ? <SampleBadge /> : null}
      {document.data ? <FileView blob={document.data} contentType="application/pdf" title={t('doc:public.sign_title')} /> : null}
      <Stack direction="row" spacing={1}>
        <Button variant={mode === 'draw' ? 'contained' : 'outlined'} onClick={() => setMode('draw')} data-testid="public-sign-mode-draw">
          {t('doc:public.sign_draw')}
        </Button>
        {subject.data.allowOtpSignature ? (
          <Button variant={mode === 'otp' ? 'contained' : 'outlined'} onClick={() => setMode('otp')} data-testid="public-sign-mode-otp">
            {t('doc:public.sign_otp')}
          </Button>
        ) : null}
      </Stack>
      {mode === 'draw' ? (
        <SignaturePad label={t('doc:signer_role.Patient')} testId="public-signature" onChange={setSignature} />
      ) : (
        <Stack spacing={1}>
          <Button onClick={() => sendSigning.mutate()} disabled={sendSigning.isPending} data-testid="public-send-signing-otp" sx={{ alignSelf: 'flex-start' }}>
            {t('doc:public.send_signing_otp')}
          </Button>
          {otp ? <TextField label={t('doc:public.otp_code')} value={code} onChange={(event) => setCode(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'public-signing-code', inputMode: 'numeric', dir: 'ltr', maxLength: 6 } }} /> : null}
          {sendSigning.error ? <ErrorState error={sendSigning.error} compact /> : null}
        </Stack>
      )}
      {sign.error ? <ErrorState error={sign.error} compact /> : null}
      <Button variant="contained" size="large" disabled={!readOk || !signOk || sign.isPending} onClick={() => sign.mutate()} data-testid="public-sign-submit" sx={{ alignSelf: 'flex-start' }}>
        {t('doc:consent.sign')}
      </Button>
    </Stack>
  );
}

export function PublicSignaturePage() {
  const { t } = useTranslation('doc');
  const { token = '' } = useParams();
  const info = useLinkInfo('signatures', token);
  return (
    <Frame screenId={SCR_PUBLIC_011} title={t('doc:public.sign_title')} tenantName={info.data?.tenantName}>
      <LinkState info={info.data} error={info.error} doneKey="doc:public.sign_done">
        {info.data ? <OtpGate segment="signatures" token={token} info={info.data}>{(session) => <PublicSignBody token={token} session={session} />}</OtpGate> : null}
      </LinkState>
    </Frame>
  );
}

function PublicShareBody({ token, session }: { token: string; session: string }) {
  const { t } = useTranslation('doc');
  const api = usePublicApi();
  const headers = { [SESSION_HEADER]: session };
  const view = useQuery({
    queryKey: ['documents-public', 'share', token],
    queryFn: () => unwrap(api.GET('/api/v1/documents/public/shares/{token}/document', { params: { path: { token } }, headers })),
  });
  const content = useQuery({
    queryKey: ['documents-public', 'share-content', token],
    queryFn: async () => {
      const result = await api.GET('/api/v1/documents/public/shares/{token}/content', { params: { path: { token }, query: {} }, headers, parseAs: 'blob' });
      if (!result.response.ok) {
        throw (result.error as unknown) ?? new Error('content');
      }
      return result.data as unknown as Blob;
    },
    enabled: !!view.data,
  });
  const download = useMutation({
    mutationFn: async () => {
      const result = await api.GET('/api/v1/documents/public/shares/{token}/content', { params: { path: { token }, query: { download: true } }, headers, parseAs: 'blob' });
      if (!result.response.ok) {
        throw (result.error as unknown) ?? new Error('download');
      }
      const blob = result.data as unknown as Blob;
      if (typeof URL.createObjectURL === 'function') {
        const url = URL.createObjectURL(blob);
        const anchor = window.document.createElement('a');
        anchor.href = url;
        anchor.download = view.data?.fileName ?? 'document';
        anchor.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    },
  });
  if (view.error) {
    return <ErrorState error={view.error} />;
  }
  if (!view.data) {
    return <LoadingState rows={3} />;
  }
  return (
    <Stack spacing={2} data-testid="public-share">
      <Typography variant="h6" component="h2">
        {view.data.title ?? view.data.fileName}
      </Typography>
      {content.data ? <FileView blob={content.data} contentType={view.data.contentType} title={view.data.fileName} /> : <LoadingState rows={2} />}
      <Button variant="outlined" onClick={() => download.mutate()} disabled={download.isPending} data-testid="public-share-download" sx={{ alignSelf: 'flex-start' }}>
        {t('doc:actions.download')}
      </Button>
      {download.error ? <ErrorState error={download.error} compact /> : null}
    </Stack>
  );
}

export function PublicSharePage() {
  const { t } = useTranslation('doc');
  const { token = '' } = useParams();
  const info = useLinkInfo('shares', token);
  return (
    <Frame screenId={SCR_PUBLIC_012} title={t('doc:public.share_title')} tenantName={info.data?.tenantName}>
      <LinkState info={info.data} error={info.error} doneKey="doc:public.share_done">
        {info.data ? <OtpGate segment="shares" token={token} info={info.data}>{(session) => <PublicShareBody token={token} session={session} />}</OtpGate> : null}
      </LinkState>
    </Frame>
  );
}
